import { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Box,
  Typography,
  Checkbox,
  Collapse,
  Button,
  Chip,
  IconButton,
  FormControlLabel,
  Paper,
  Divider,
  TextField,
  MenuItem,
  CircularProgress
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import {
  DEFAULT_CATALOG,
  type TestPointCatalog,
  type FunctionSpec,
  type RangeSpec,
  type TestPoint
} from '../data/defaultCatalog';
import { useEquipmentCatalogsQuery } from '@api/bff/equipmentCatalogs';
import { generateStepsFromTestPoints } from '../utils/generateStepsFromTestPoints';
import type { ScriptStep } from '../types/steps';

export type SelectedTestPoint = {
  id: string;
  func: FunctionSpec;
  range: RangeSpec;
  testPoint: TestPoint;
};

export type CatalogSelection = {
  catalogId: string | null;
  catalogName: string;
  modelVariant: string | null;
  firmwareCommand: string | null;
};

type Props = {
  onGenerate?: (steps: ScriptStep[]) => void;
  onSelectionChange?: (entries: SelectedTestPoint[]) => void;
  onCatalogChange?: (selection: CatalogSelection) => void;
  disabledTestPointIds?: string[];
  hideGenerateButton?: boolean;
};

type SelectedMap = Record<string, boolean>; // testPointId → selected

const parseFunctionsJson = (json: string): FunctionSpec[] | null => {
  try {
    const parsed = JSON.parse(json);
    if (Array.isArray(parsed)) return parsed as FunctionSpec[];
    return null;
  } catch {
    return null;
  }
};

const parseModelsJson = (json: string): string[] => {
  try {
    const parsed = JSON.parse(json);
    if (Array.isArray(parsed)) return parsed.filter(m => typeof m === 'string');
    return [];
  } catch {
    return [];
  }
};

export const TestPointPicker = ({
  onGenerate,
  onSelectionChange,
  onCatalogChange,
  disabledTestPointIds = [],
  hideGenerateButton = false
}: Props) => {
  const { data: apiCatalogs, isLoading: catalogsLoading } =
    useEquipmentCatalogsQuery();
  const [selectedCatalogId, setSelectedCatalogId] = useState<string | null>(
    null
  );
  const [selectedModelVariant, setSelectedModelVariant] = useState<
    string | null
  >(null);

  const activeApiCatalog = useMemo(
    () => apiCatalogs?.find(c => c.id === selectedCatalogId) ?? null,
    [apiCatalogs, selectedCatalogId]
  );

  const modelVariants = useMemo(
    () => (activeApiCatalog ? parseModelsJson(activeApiCatalog.modelsJson) : []),
    [activeApiCatalog]
  );

  // Auto-select first catalog when list loads (if API returned any)
  useEffect(() => {
    if (apiCatalogs && apiCatalogs.length > 0 && selectedCatalogId === null) {
      setSelectedCatalogId(apiCatalogs[0].id);
    }
  }, [apiCatalogs, selectedCatalogId]);

  // Auto-select first model variant whenever the catalog changes
  useEffect(() => {
    if (modelVariants.length > 0) {
      setSelectedModelVariant(modelVariants[0]);
    } else {
      setSelectedModelVariant(null);
    }
  }, [modelVariants]);

  const catalog: TestPointCatalog = useMemo(() => {
    if (!activeApiCatalog) return DEFAULT_CATALOG;
    const fns = parseFunctionsJson(activeApiCatalog.functionsJson);
    if (!fns) return DEFAULT_CATALOG;
    return {
      model: selectedModelVariant ?? activeApiCatalog.name,
      standard: 'ISO 17025',
      functions: fns
    };
  }, [activeApiCatalog, selectedModelVariant]);

  // Notify parent of catalog selection (for firmwareCommand)
  useEffect(() => {
    if (!onCatalogChange) return;
    onCatalogChange({
      catalogId: activeApiCatalog?.id ?? null,
      catalogName: activeApiCatalog?.name ?? DEFAULT_CATALOG.model,
      modelVariant: selectedModelVariant,
      firmwareCommand: activeApiCatalog?.firmwareCommand ?? null
    });
  }, [activeApiCatalog, selectedModelVariant, onCatalogChange]);

  const [selected, setSelected] = useState<SelectedMap>({});
  const [expandedFunctions, setExpandedFunctions] = useState<
    Record<string, boolean>
  >({});
  const [expandedRanges, setExpandedRanges] = useState<Record<string, boolean>>(
    {}
  );

  // Count helpers
  const allTestPointIds = useMemo(() => {
    const ids: string[] = [];
    for (const func of catalog.functions) {
      for (const range of func.ranges) {
        for (const tp of range.testPoints) {
          ids.push(tp.id);
        }
      }
    }
    return ids;
  }, [catalog]);

  const selectedCount = useMemo(
    () => allTestPointIds.filter(id => selected[id]).length,
    [allTestPointIds, selected]
  );

  const getFunctionTestPointIds = useCallback(
    (func: FunctionSpec) =>
      func.ranges.flatMap(r => r.testPoints.map(tp => tp.id)),
    []
  );

  const getRangeTestPointIds = useCallback(
    (range: RangeSpec) => range.testPoints.map(tp => tp.id),
    []
  );

  const isFunctionSelected = useCallback(
    (func: FunctionSpec) => {
      const ids = getFunctionTestPointIds(func);
      return ids.length > 0 && ids.every(id => selected[id]);
    },
    [selected, getFunctionTestPointIds]
  );

  const isFunctionIndeterminate = useCallback(
    (func: FunctionSpec) => {
      const ids = getFunctionTestPointIds(func);
      const count = ids.filter(id => selected[id]).length;
      return count > 0 && count < ids.length;
    },
    [selected, getFunctionTestPointIds]
  );

  const isRangeSelected = useCallback(
    (range: RangeSpec) => {
      const ids = getRangeTestPointIds(range);
      return ids.length > 0 && ids.every(id => selected[id]);
    },
    [selected, getRangeTestPointIds]
  );

  const isRangeIndeterminate = useCallback(
    (range: RangeSpec) => {
      const ids = getRangeTestPointIds(range);
      const count = ids.filter(id => selected[id]).length;
      return count > 0 && count < ids.length;
    },
    [selected, getRangeTestPointIds]
  );

  const emitSelection = useCallback(
    (sel: SelectedMap) => {
      if (!onSelectionChange) return;
      const entries: SelectedTestPoint[] = [];
      for (const func of catalog.functions) {
        for (const range of func.ranges) {
          for (const tp of range.testPoints) {
            if (sel[tp.id]) {
              entries.push({ id: tp.id, func, range, testPoint: tp });
            }
          }
        }
      }
      onSelectionChange(entries);
    },
    [catalog, onSelectionChange]
  );

  const toggleTestPoint = useCallback(
    (id: string) => {
      if (disabledTestPointIds.includes(id)) return;
      setSelected(prev => {
        const next = { ...prev, [id]: !prev[id] };
        emitSelection(next);
        return next;
      });
    },
    [disabledTestPointIds, emitSelection]
  );

  const toggleRange = useCallback(
    (range: RangeSpec) => {
      const ids = getRangeTestPointIds(range).filter(
        id => !disabledTestPointIds.includes(id)
      );
      const allSelected = ids.every(id => selected[id]);
      setSelected(prev => {
        const next = { ...prev };
        ids.forEach(id => {
          next[id] = !allSelected;
        });
        emitSelection(next);
        return next;
      });
    },
    [selected, getRangeTestPointIds, disabledTestPointIds, emitSelection]
  );

  const toggleFunction = useCallback(
    (func: FunctionSpec) => {
      const ids = getFunctionTestPointIds(func).filter(
        id => !disabledTestPointIds.includes(id)
      );
      const allSelected = ids.every(id => selected[id]);
      setSelected(prev => {
        const next = { ...prev };
        ids.forEach(id => {
          next[id] = !allSelected;
        });
        emitSelection(next);
        return next;
      });
    },
    [selected, getFunctionTestPointIds, disabledTestPointIds, emitSelection]
  );

  const selectAll = useCallback(() => {
    const next: SelectedMap = {};
    allTestPointIds.forEach(id => {
      if (!disabledTestPointIds.includes(id)) {
        next[id] = true;
      }
    });
    setSelected(next);
    emitSelection(next);
  }, [allTestPointIds, disabledTestPointIds, emitSelection]);

  const selectNone = useCallback(() => {
    setSelected({});
    emitSelection({});
  }, [emitSelection]);

  const toggleFunctionExpanded = useCallback((funcId: string) => {
    setExpandedFunctions(prev => ({ ...prev, [funcId]: !prev[funcId] }));
  }, []);

  const toggleRangeExpanded = useCallback((key: string) => {
    setExpandedRanges(prev => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const handleGenerate = useCallback(() => {
    const selectedPoints: {
      func: FunctionSpec;
      range: RangeSpec;
      testPoint: TestPoint;
    }[] = [];
    for (const func of catalog.functions) {
      for (const range of func.ranges) {
        for (const tp of range.testPoints) {
          if (selected[tp.id]) {
            selectedPoints.push({ func, range, testPoint: tp });
          }
        }
      }
    }
    const steps = generateStepsFromTestPoints(selectedPoints);
    onGenerate?.(steps);
  }, [catalog, selected, onGenerate]);

  const hasApiCatalogs = !!apiCatalogs && apiCatalogs.length > 0;
  const showVariantPicker = modelVariants.length > 1;

  return (
    <Box>
      {(hasApiCatalogs || catalogsLoading) && (
        <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
          {catalogsLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="caption" color="text.secondary">
                Loading catalogs...
              </Typography>
            </Box>
          )}
          {hasApiCatalogs && (
            <TextField
              select
              label="Catalog"
              size="small"
              value={selectedCatalogId ?? ''}
              onChange={e => {
                setSelectedCatalogId(e.target.value || null);
                setSelected({});
                if (onSelectionChange) onSelectionChange([]);
              }}
              sx={{ minWidth: 220 }}
            >
              {apiCatalogs!.map(c => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </TextField>
          )}
          {showVariantPicker && (
            <TextField
              select
              label="Model variant"
              size="small"
              value={selectedModelVariant ?? ''}
              onChange={e => setSelectedModelVariant(e.target.value || null)}
              sx={{ minWidth: 160 }}
            >
              {modelVariants.map(m => (
                <MenuItem key={m} value={m}>
                  {m}
                </MenuItem>
              ))}
            </TextField>
          )}
          {activeApiCatalog?.firmwareCommand && (
            <Chip
              label={`Firmware: ${activeApiCatalog.firmwareCommand}`}
              size="small"
              variant="outlined"
              color="info"
            />
          )}
        </Box>
      )}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2
        }}
      >
        <Box>
          <Typography variant="subtitle2">
            Test Points — {catalog.model}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {selectedCount} of {allTestPointIds.length} selected
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button size="small" onClick={selectAll}>
            Select All
          </Button>
          <Button size="small" onClick={selectNone}>
            Clear
          </Button>
        </Box>
      </Box>

      {catalog.functions.map(func => {
        const funcCount = getFunctionTestPointIds(func).filter(
          id => selected[id]
        ).length;
        const funcTotal = getFunctionTestPointIds(func).length;
        const isExpanded = expandedFunctions[func.id] ?? false;

        return (
          <Paper key={func.id} variant="outlined" sx={{ mb: 1 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                px: 1.5,
                py: 0.5,
                cursor: 'pointer',
                '&:hover': { bgcolor: 'action.hover' }
              }}
              onClick={() => toggleFunctionExpanded(func.id)}
            >
              <Checkbox
                checked={isFunctionSelected(func)}
                indeterminate={isFunctionIndeterminate(func)}
                onClick={e => e.stopPropagation()}
                onChange={() => toggleFunction(func)}
                size="small"
              />
              <Typography variant="body2" sx={{ fontWeight: 600, flex: 1 }}>
                {func.name} ({func.id.toUpperCase()})
              </Typography>
              <Chip
                label={`${funcCount}/${funcTotal}`}
                size="small"
                color={
                  funcCount === funcTotal
                    ? 'primary'
                    : funcCount > 0
                      ? 'warning'
                      : 'default'
                }
                variant="outlined"
                sx={{ mr: 1 }}
              />
              <IconButton size="small">
                {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>

            <Collapse in={isExpanded}>
              <Divider />
              <Box sx={{ pl: 2 }}>
                {func.ranges.map(range => {
                  const rangeKey = `${func.id}-${range.id}`;
                  const rangeExpanded = expandedRanges[rangeKey] ?? true;
                  const rangeCount = getRangeTestPointIds(range).filter(
                    id => selected[id]
                  ).length;

                  return (
                    <Box key={range.id}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          py: 0.25,
                          cursor: 'pointer',
                          '&:hover': { bgcolor: 'action.hover' }
                        }}
                        onClick={() => toggleRangeExpanded(rangeKey)}
                      >
                        <Checkbox
                          checked={isRangeSelected(range)}
                          indeterminate={isRangeIndeterminate(range)}
                          onClick={e => e.stopPropagation()}
                          onChange={() => toggleRange(range)}
                          size="small"
                        />
                        <Typography variant="body2" sx={{ flex: 1 }}>
                          {range.id} range
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ mr: 1 }}
                        >
                          {rangeCount}/{range.testPoints.length}
                        </Typography>
                        <IconButton size="small">
                          {rangeExpanded ? (
                            <ExpandLessIcon fontSize="small" />
                          ) : (
                            <ExpandMoreIcon fontSize="small" />
                          )}
                        </IconButton>
                      </Box>

                      <Collapse in={rangeExpanded}>
                        <Box sx={{ pl: 4, pb: 0.5 }}>
                          {range.testPoints.map(tp => {
                            const isDisabled = disabledTestPointIds.includes(
                              tp.id
                            );
                            return (
                              <FormControlLabel
                                key={tp.id}
                                sx={{
                                  display: 'block',
                                  ml: 0,
                                  opacity: isDisabled ? 0.5 : 1
                                }}
                                disabled={isDisabled}
                                control={
                                  <Checkbox
                                    checked={!!selected[tp.id] || isDisabled}
                                    onChange={() => toggleTestPoint(tp.id)}
                                    size="small"
                                    disabled={isDisabled}
                                  />
                                }
                                label={
                                  <Typography variant="body2" component="span">
                                    {tp.label}
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                      component="span"
                                      sx={{ ml: 1 }}
                                    >
                                      (nominal: {tp.nominal})
                                      {isDisabled && ' — already added'}
                                    </Typography>
                                  </Typography>
                                }
                              />
                            );
                          })}
                        </Box>
                      </Collapse>
                    </Box>
                  );
                })}
              </Box>
            </Collapse>
          </Paper>
        );
      })}

      {!hideGenerateButton && (
        <Box
          sx={{
            mt: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {selectedCount} test points &rarr; {selectedCount * 4} steps (~
            {Math.ceil((selectedCount * 4 * 4.5) / 60)} min est.)
          </Typography>
          <Button
            variant="contained"
            startIcon={<AutoFixHighIcon />}
            onClick={handleGenerate}
            disabled={selectedCount === 0}
          >
            Generate {selectedCount * 4} Steps
          </Button>
        </Box>
      )}
      {hideGenerateButton && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {selectedCount} test point{selectedCount !== 1 ? 's' : ''} selected
          </Typography>
        </Box>
      )}
    </Box>
  );
};
